import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { AdminBrandDetail } from './AdminBrandDetail';
import { AdminBrandsList } from './AdminBrandsList';
import { AdminCatalogBrowser } from './AdminCatalogBrowser';
import { AdminDashboard } from './AdminDashboard';
import { AdminSkillEditor } from './AdminSkillEditor';
import { AdminSkillsList } from './AdminSkillsList';
import { listAdminBrands, listAdminSkills } from './google-ads-audit-admin-api';

const AdminView = {
  DASHBOARD: 'dashboard',
  SKILLS: 'skills',
  SKILL_EDITOR: 'skill-editor',
  BRANDS: 'brands',
  BRAND_DETAIL: 'brand-detail',
  CATALOG: 'catalog'
} as const;
type AdminViewType = (typeof AdminView)[keyof typeof AdminView];

const NAV_ITEMS: { key: AdminViewType; label: string }[] = [
  { key: AdminView.DASHBOARD, label: 'Dashboard' },
  { key: AdminView.SKILLS, label: 'Skills' },
  { key: AdminView.BRANDS, label: 'Brands' },
  { key: AdminView.CATALOG, label: 'Field Catalog' }
];

export const AdminSection = () => {
  const [view, setView] = useState<AdminViewType>(AdminView.DASHBOARD);
  const [selectedSkillId, setSelectedSkillId] = useState<string | undefined>(undefined);
  const [selectedSectionId, setSelectedSectionId] = useState<string | undefined>(undefined);
  const [selectedBrandId, setSelectedBrandId] = useState<string | undefined>(undefined);

  const { data: skills = [], refetch: refetchSkills } = useQuery({
    queryKey: ['google-ads-audit-admin-skills'],
    queryFn: () => listAdminSkills()
  });
  const { data: brands = [] } = useQuery({
    queryKey: ['google-ads-audit-admin-brands'],
    queryFn: () => listAdminBrands()
  });

  return (
    <div className='google-ads-audit-admin-section'>
      <nav className='google-ads-audit-admin-nav'>
        {NAV_ITEMS.map(item => (
          <InteractiveDiv
            key={item.key}
            className={view === item.key ? 'active' : ''}
            onClick={() => setView(item.key)}
          >
            {item.label}
          </InteractiveDiv>
        ))}
      </nav>
      <div className='google-ads-audit-admin-content'>
        {view === AdminView.DASHBOARD && (
          <AdminDashboard
            onOpenSkill={id => {
              setSelectedSkillId(id);
              setView(AdminView.SKILL_EDITOR);
            }}
            onOpenSection={sectionId => {
              setSelectedSectionId(sectionId);
              setView(AdminView.SKILLS);
            }}
            onNewSkill={() => {
              setSelectedSkillId(undefined);
              setView(AdminView.SKILL_EDITOR);
            }}
          />
        )}
        {view === AdminView.SKILLS && (
          <AdminSkillsList
            skills={skills}
            initialSectionId={selectedSectionId}
            onOpenSkill={id => {
              setSelectedSkillId(id);
              setView(AdminView.SKILL_EDITOR);
            }}
          />
        )}
        {view === AdminView.SKILL_EDITOR && (
          <AdminSkillEditor
            skillId={selectedSkillId}
            onSaved={() => {
              refetchSkills();
              setView(AdminView.SKILLS);
            }}
            onCancel={() => setView(AdminView.SKILLS)}
          />
        )}
        {view === AdminView.BRANDS && (
          <AdminBrandsList
            brands={brands}
            onOpenBrand={id => {
              setSelectedBrandId(id);
              setView(AdminView.BRAND_DETAIL);
            }}
          />
        )}
        {view === AdminView.BRAND_DETAIL && selectedBrandId && (
          <AdminBrandDetail
            brandId={selectedBrandId}
            onBack={() => setView(AdminView.BRANDS)}
            onDeleted={() => setView(AdminView.BRANDS)}
          />
        )}
        {view === AdminView.CATALOG && <AdminCatalogBrowser />}
      </div>
    </div>
  );
};
